import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  defaultDescription,
  productRouteMetadata,
  routeMetadata as publishedRouteMetadata,
  type RouteMetadataEntry,
} from "./route-metadata-catalog";

const publicOrigin = "https://lifescapsule.jpwlabs.com";
const developmentRouteMetadata: Record<string, { title: string; description: string }> = import.meta
  .env.DEV
  ? {
      "/app/studio-poc": {
        title: "Interactive Book Studio — Life's Capsule",
        description:
          "Turn a memory or idea into a children’s book. Shape the words, choose the pictures and read every page before approving it.",
      },
    }
  : {};

const routeMetadata: Record<string, RouteMetadataEntry> = {
  ...developmentRouteMetadata,
  ...publishedRouteMetadata,
};

function setMeta(attribute: "name" | "property", key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const meta = document.querySelector<HTMLMetaElement>(selector) ?? document.createElement("meta");
  meta.setAttribute(attribute, key);
  meta.setAttribute("content", content);
  if (!meta.isConnected) document.head.append(meta);
}

function setCanonical(pathname: string) {
  const canonical =
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]') ??
    document.createElement("link");
  canonical.setAttribute("rel", "canonical");
  canonical.setAttribute("href", `${publicOrigin}${pathname === "/" ? "/" : pathname}`);
  if (!canonical.isConnected) document.head.append(canonical);
}

function canonicalUrl(pathname: string) {
  return `${publicOrigin}${pathname === "/" ? "/" : pathname}`;
}

function setRobots(content: "index,follow" | "noindex,nofollow") {
  setMeta("name", "robots", content);
}

export function RouteMetadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const privateShareRoute = pathname.startsWith("/listen/");
    const familyWorkspaceRoute = /^\/app\/books\/[^/]+\/family\/?$/.test(pathname);
    const bookStudioRoute = /^\/app\/books\/[^/]+\/studio(?:\/.*)?$/.test(pathname);
    const guidedBookRoute = /^\/app\/books\/[^/]+\/guided\/?$/.test(pathname);
    const bookToolsRoute = /^\/app\/books\/[^/]+\/tools\/?$/.test(pathname);
    const bookProductionRoute = /^\/app\/books\/[^/]+\/production\/?$/.test(pathname);
    const bookOutputRoute = /^\/app\/books\/[^/]+\/output\/?$/.test(pathname);
    const printSimulationRoute = /^\/app\/books\/[^/]+\/print-simulation\/?$/.test(pathname);
    const captureWorkspaceRoute = /^\/app\/books\/[^/]+\/capture\/?$/.test(pathname);
    const archiveWorkspaceRoute = /^\/app\/books\/[^/]+\/archive\/?$/.test(pathname);
    const deliveryWorkspaceRoute = /^\/app\/books\/[^/]+\/delivery\/?$/.test(pathname);
    const illustrationDirectorRoute = /^\/app\/books\/[^/]+\/illustration-director\/?$/.test(
      pathname,
    );
    const guidedInterviewRoute = /^\/app\/books\/[^/]+\/interviews\/[^/]+\/?$/.test(pathname);
    const narrationStudioRoute = /^\/app\/books\/[^/]+\/narration\/?$/.test(pathname);
    const bookQualityRoute = /^\/app\/books\/[^/]+\/quality\/?$/.test(pathname);
    const editionReorderRoute = /^\/app\/books\/[^/]+\/reorders\/?$/.test(pathname);
    const privateReaderRoute = /^\/app\/books\/[^/]+\/reader\/?$/.test(pathname);
    const privateStorySearchRoute = /^\/app\/books\/[^/]+\/search\/?$/.test(pathname);
    const familyMapRoute = /^\/app\/books\/[^/]+\/family-map\/?$/.test(pathname);
    const familyNotificationsRoute = /^\/app\/books\/[^/]+\/notifications\/?$/.test(pathname);
    const bookTemplatesRoute = /^\/app\/book-templates\/?$/.test(pathname);
    const photoRestorationRoute = pathname === "/demo/photo-restoration";
    const giftRoute = pathname === "/gift";
    const familyReviewRoute = pathname === "/demo/family-review";
    const [, resource, templateId] = pathname.split("/");
    const template =
      resource === "books" || resource === "checkout"
        ? productRouteMetadata(templateId ?? "", resource)
        : null;
    const unknownProductRoute = resource === "books" && Boolean(templateId) && !template;
    const sensitiveRoute =
      privateShareRoute ||
      unknownProductRoute ||
      pathname.startsWith("/app") ||
      pathname.startsWith("/checkout") ||
      giftRoute ||
      familyReviewRoute ||
      pathname === "/demo/phone-capture" ||
      photoRestorationRoute ||
      pathname === "/demo/prompt-campaigns" ||
      [
        "/sign-in",
        "/sign-in/complete",
        "/redeem",
        "/resume",
        "/invitations/accept",
        "/support/approve",
        "/unsubscribe",
      ].includes(pathname);
    const metadata = printSimulationRoute
      ? {
          title: "Simulated print journey — Life's Capsule",
          description: "Return to the connected book tools while printed ordering is unavailable.",
        }
      : bookProductionRoute
        ? {
            title: "Whole-book production — Life's Capsule",
            description:
              "Follow your book from original memories through story drafts, your reviews and finished pages.",
          }
        : bookOutputRoute
          ? {
              title: "Finish and export — Life's Capsule",
              description:
                "Arrange approved chapters, run output checks, and request a private exact-version review PDF.",
            }
          : bookToolsRoute
            ? {
                title: "Book home — Life's Capsule",
                description:
                  "Open a living family book, continue its next memory, and see private family progress.",
              }
            : illustrationDirectorRoute
              ? {
                  title: "Illustration Director — Life's Capsule",
                  description:
                    "Return to the connected tools for this private Life's Capsule book.",
                }
              : deliveryWorkspaceRoute
                ? {
                    title: "Delivery controls — Life's Capsule",
                    description:
                      "Review a private, consent-led delivery boundary with external adapters unavailable.",
                  }
                : archiveWorkspaceRoute
                  ? {
                      title: "Private archive — Life's Capsule",
                      description:
                        "Return to the connected tools for this private Life's Capsule book.",
                    }
                  : captureWorkspaceRoute
                    ? {
                        title: "Capture workspace — Life's Capsule",
                        description:
                          "Return to the connected tools for this private Life's Capsule book.",
                      }
                    : guidedBookRoute
                      ? {
                          title: "Your book — Life's Capsule",
                          description: "Simple steps from your ideas to your own finished book.",
                        }
                      : bookStudioRoute
                        ? {
                            title: "Book Studio — Life's Capsule",
                            description:
                              "Arrange approved chapters and inspect export readiness in a private book workspace.",
                          }
                        : familyWorkspaceRoute
                          ? {
                              title: "Family workspace — Life's Capsule",
                              description:
                                "Return to the connected tools for this private Life's Capsule book.",
                            }
                          : template
                            ? template
                            : privateShareRoute
                              ? {
                                  title: "Opening a family memory — Life's Capsule",
                                  description: "Open a revocable Life's Capsule family recording.",
                                }
                              : pathname.startsWith("/app/books/")
                                ? {
                                    title: "Private book workspace — Life's Capsule",
                                    description:
                                      "Add memories and invite family inside a role-protected book workspace.",
                                  }
                                : (routeMetadata[pathname] ?? {
                                    title: "Page not found — Life's Capsule",
                                    description: defaultDescription,
                                  });

    const integratedMetadata = guidedInterviewRoute
      ? {
          title: "Guided interviews — Life's Capsule",
          description: "Return to the connected tools for this private Life's Capsule book.",
        }
      : narrationStudioRoute
        ? {
            title: "Narration — Life's Capsule",
            description: "Return to the connected tools for this private Life's Capsule book.",
          }
        : metadata;

    const batchBMetadata = familyReviewRoute
      ? {
          title: "Family story example — Life's Capsule",
          description:
            "Explore how family roles, contributions, and review come together in one book.",
        }
      : giftRoute
        ? {
            title: "Activate a gift — Life's Capsule",
            description: "Activate an existing Life's Capsule gift voucher for a private book.",
          }
        : photoRestorationRoute
          ? {
              title: "Photo restoration — Life's Capsule",
              description: "Return to the available Life's Capsule story examples.",
            }
          : bookTemplatesRoute
            ? {
                title: "Book formats — Life's Capsule",
                description: "Return to the connected books and formats available to your account.",
              }
            : bookQualityRoute
              ? {
                  title: "Book review — Life's Capsule",
                  description:
                    "Return to the connected tools for this private Life's Capsule book.",
                }
              : editionReorderRoute
                ? {
                    title: "Additional copies — Life's Capsule",
                    description:
                      "Return to the connected book tools while printed ordering is unavailable.",
                  }
                : privateReaderRoute
                  ? {
                      title: "Private reading room — Life's Capsule",
                      description:
                        "Read your book’s approved pages. Original memories and unfinished drafts stay private.",
                    }
                  : privateStorySearchRoute
                    ? {
                        title: "Find a memory — Life's Capsule",
                        description:
                          "Find a word or memory in the approved pages of your private book. Your search stays in this browser.",
                      }
                    : familyMapRoute
                      ? {
                          title: "Family relationships — Life's Capsule",
                          description:
                            "Return to the connected tools for this private Life's Capsule book.",
                        }
                      : familyNotificationsRoute
                        ? {
                            title: "Family notifications — Life's Capsule",
                            description:
                              "Return to the connected tools for this private Life's Capsule book.",
                          }
                        : integratedMetadata;

    document.title = batchBMetadata.title;
    setMeta("name", "description", batchBMetadata.description);
    setMeta("property", "og:title", batchBMetadata.title);
    setMeta("property", "og:description", batchBMetadata.description);
    const canonicalPath = privateShareRoute ? "/listen" : pathname;
    setCanonical(canonicalPath);
    setMeta("property", "og:url", canonicalUrl(canonicalPath));
    setRobots(sensitiveRoute ? "noindex,nofollow" : "index,follow");
  }, [pathname]);

  return null;
}
